import type { CSSProperties } from "react";
import { CheckIcon } from "@/components/icons";
import { actionProps, scopeProps } from "@/lib/controlAnalytics";
import type { GateKind } from "@/lib/gate";
import { useGate } from "./gateContext";
import { GRAD_BUTTON, OUTLINE_BUTTON } from "./styles";

interface Locked {
    title: string;
    line: string;
    checks: [string, string, string];
    action: string;
    gate: GateKind;
    /** The preview's three rows: each one's chip. */
    chips: [string, string, string];
}

/**
 * The Account page, for a visitor: what it holds. The places in the nav
 * show the example instead (./previews).
 */
const LOCKED: Locked = {
    title: "Your account",
    line: "Where everything you keep on Job Envoy stands.",
    checks: [
        "Saved jobs and applications",
        "Your resume and your envoy",
        "Your plan and your emails",
    ],
    action: "Create free account",
    gate: "join",
    chips: ["Open", "Ready", "Sent"],
};

const WIDTHS = ["70%", "55%", "62%"];

/**
 * The Account page, signed out: what it is, three lines on what it holds,
 * and one way in. The sheet comes when the action is pressed.
 */
export default function LockedPlace() {
    const locked = LOCKED;
    const kind = "account";
    const { ask } = useGate();
    return (
        <section
            {...scopeProps({ source: "LockedPlace", section: kind })}
            aria-labelledby="locked-title"
            className="grid grid-cols-2 items-center gap-10 py-8 max-[860px]:grid-cols-1 max-[860px]:gap-7"
        >
            <div className="flex flex-col gap-4">
                <span className="self-start rounded-full bg-card-raised px-2.5 py-1 text-[12px] font-semibold text-fg-muted">
                    With a free account
                </span>
                <h1
                    id="locked-title"
                    className="m-0 font-display text-[40px] font-extrabold leading-[1.06] tracking-[-0.03em] max-[760px]:text-[32px]"
                >
                    {locked.title}
                </h1>
                <p className="m-0 text-[17px] text-fg-muted">{locked.line}</p>
                <ul className="m-0 flex list-none flex-col gap-2.5 p-0 text-[15px]">
                    {locked.checks.map((check) => (
                        <li key={check} className="flex items-center gap-2.5">
                            <span className="grid size-[22px] shrink-0 place-items-center rounded-full bg-tint-green text-brand-ink">
                                <CheckIcon className="size-3" strokeWidth={3} />
                            </span>
                            {check}
                        </li>
                    ))}
                </ul>
                <div className="flex flex-wrap gap-2.5 pt-1">
                    <button
                        {...actionProps("join_signup_clicked", {
                            source: "LockedPlace",
                            section: kind,
                        })}
                        type="button"
                        onClick={() => ask(locked.gate)}
                        className={`${GRAD_BUTTON} h-[46px] rounded-[11px] px-5 text-[14px]`}
                    >
                        {locked.action}
                    </button>
                    <button
                        {...actionProps("join_signin_clicked", {
                            source: "LockedPlace",
                            section: kind,
                        })}
                        type="button"
                        onClick={() => ask("signin")}
                        className={`${OUTLINE_BUTTON} h-[46px] rounded-[11px] px-3.5 text-[14px]`}
                    >
                        Sign in
                    </button>
                </div>
            </div>
            <div
                aria-hidden="true"
                className="relative overflow-hidden rounded-2xl border border-line bg-card p-[18px] shadow-card"
            >
                {locked.chips.map((chip, index) => (
                    <div
                        key={index}
                        className="flow-step flex items-center gap-3 border-t border-line-soft py-3"
                        style={{ "--step": index } as CSSProperties}
                    >
                        <span className="flex flex-1 flex-col gap-1.5">
                            <span
                                className="block h-2.5 rounded-[5px] bg-line-strong"
                                style={{ width: WIDTHS[index] }}
                            />
                            <span className="block h-2 w-2/5 rounded bg-line" />
                        </span>
                        <span className="rounded-full bg-applied px-2.5 py-[3px] text-[11px] font-semibold text-applied-fg">
                            {chip}
                        </span>
                    </div>
                ))}
                <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_30%,var(--color-card)_100%)]" />
            </div>
        </section>
    );
}
